import { useEffect, useState, useRef } from "react";
import Papa from "papaparse";
import api from "../services/api";
import Table from "../components/common/Table";
import SidebarRight from "../components/common/SidebarRight";
import Modal from "../components/common/Modal";
import Autocomplete from "../components/common/Autocomplete";
import UserEdit from "../components/other/UserEdit";
import { useAuth } from "../context/useAuth";

export default function UserList() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState({
    isArchived: false,
  });
  const [showSidebar, setShowSidebar] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const actionsDropdownRef = useRef(null);
  const [showModalEdit, setShowModalEdit] = useState();
  const [editItem, setEditItem] = useState();
  const [rolesMap, setRolesMap] = useState({});
  const { user, checkPrivileges } = useAuth();

  // Close Actions dropdown when clicking outside
  useEffect(() => {
    if (!showActions) return;
    function handleClickOutside(event) {
      if (actionsDropdownRef.current && !actionsDropdownRef.current.contains(event.target)) {
        setShowActions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showActions]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const rolesRes = await api.getRoles();
        if (rolesRes.status === 200) {
          const map = {};
          rolesRes.data.forEach((role) => {
            map[role.id] = role;
          });
          setRolesMap(map);
        }
      } catch (error) {
        console.error("Error fetching roles", error);
      }
    };
    fetchRoles();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      let data = await api.getUsers();
      setList(data.data);
    };

    fetchData();
  }, [user]);

  const createUser = () => {
    setEditItem({
      visibilityRole: [],
    });
    setShowModalEdit(true);
  };

  const exportUsers = async () => {
    const response = await api.exportUsers();
    if (response.ok && response.data && response.data.data) {
      const { columns, data } = response.data;
      const csv = Papa.unparse({
        fields: columns,
        data: data.map((row) => columns.map((col) => row[col])),
      });

      let fileTitle = "MOS - Export Users";

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${fileTitle}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  };

  const config = {
    onClickRow: (item, index) => {
      setEditItem(item);
      setShowModalEdit(true);
    },
    columns: [
      {
        key: "email",
        header: "Email",
        template: (item) => <span className="main-row">{item.email || "NO EMAIL"}</span>,
      },
      {
        key: "firstName",
        header: "First Name",
        template: (item) => <span>{item.firstName || "-"}</span>,
      },
      {
        key: "lastName",
        header: "Last Name",
        template: (item) => <span>{item.lastName || "-"}</span>,
      },
      {
        key: "_employee",
        header: "HR Code",
        template: (item) => <span>{item._employee || "-"}</span>,
      },
      {
        header: "Role",
        key: "_role",
        template: (item) => <span>{rolesMap[item._role] ? rolesMap[item._role].name : item._role || "No Role"}</span>,
      },
    ],
  };

  const setFilterValue = (key, value) => {
    setFilter((prev) => {
      let set = {};
      set[key] = value;
      return { ...prev, ...set };
    });
  };

  const filteredList = list.filter((item) => {
    if (filter.isArchived !== "" && filter.isArchived !== undefined) {
      if (!!item.isArchived !== filter.isArchived) return false;
    }
    if (filter.email && !item.email?.toLowerCase().includes(filter.email.toLowerCase())) return false;
    return true;
  });

  return (
    <>
      <div className={"page-wrapper" + (showSidebar ? " show-sidebar-right" : "")}>
        <div className="btn-bar">
          <div className="btn-bar-left">
            <div className="title">Users</div>
          </div>
          <div className="btn-bar-right">
            <div className="searchbar-container">
              <input
                type="text"
                className="searchbar-input"
                placeholder="Search"
                value={filter.email || ""}
                onChange={(e) => setFilterValue("email", e.target.value)}
              />
              {filter.email && (
                <span className="searchbar-clear" onClick={() => setFilterValue("email", "")} title="Clear">
                  ×
                </span>
              )}
              <span className="searchbar-icon">
                <i className="material-icons">search</i>
              </span>
            </div>

            <button className="blue" onClick={createUser}>
              <i className="material-symbols-outlined mr-10">add</i>
              Add User
            </button>
            {checkPrivileges("editUser") && (
              <div className="dropdown" ref={actionsDropdownRef}>
                <button className="dropdown-toggle" onClick={() => setShowActions((v) => !v)}>
                  Actions <i className="material-icons">keyboard_arrow_down</i>
                </button>
                {showActions && (
                  <div className="dropdown-menu">
                    <div
                      className="dropdown-item"
                      onClick={() => {
                        setShowActions(false);
                        exportUsers();
                      }}
                    >
                      <span className="material-symbols-outlined">file_export</span>
                      <div>Export</div>
                    </div>
                  </div>
                )}
              </div>
            )}
            {!showSidebar && (
              <button className="blue-light" onClick={() => setShowSidebar(true)}>
                <span className="material-symbols-outlined">filter_alt</span>
                <div>Filters</div>
              </button>
            )}
          </div>
        </div>

        <div className="table-container">
          <Table
            config={config}
            filter={filter}
            data={filteredList}
            rowIcon="account_circle"
            rowClassName={(row) => (row.isArchived ? "archived-row" : "")}
          ></Table>
        </div>
      </div>

      <SidebarRight show={showSidebar}>
        <div className="sidebar-header">
          <div className="sidebar-title">Filters</div>
          <div className="sidebar-title-right">
            <button className="medium icon-right" onClick={() => setShowSidebar(false)}>
              Close
              <i className="material-icons">chevron_right</i>
            </button>
          </div>
        </div>
        <div className="filter-container">
          <div className="filter-row">
            <input
              type="text"
              value={filter.email || ""}
              placeholder="E-mail"
              onChange={(e) => setFilterValue("email", e.target.value)}
            />
            <button className="small btn-reset" onClick={() => setFilterValue("email", "")}>
              <i className="material-icons">close</i>
            </button>
          </div>
          <div className="filter-row">
            <Autocomplete
              allowClear={false}
              items={[
                { value: false, name: "Active" },
                { value: true, name: "Archived" },
              ]}
              value={filter.isArchived}
              onSelect={(item) => setFilterValue("isArchived", item.value)}
              placeholder="Active users"
            />
            <button className="small btn-reset" onClick={() => setFilterValue("isArchived", "")}>
              <i className="material-icons">close</i>
            </button>
          </div>
        </div>
        <div className="sidebar-footer">
          <button onClick={() => setFilter({ email: "", firstName: "", lastName: "", teamName: "" })}>
            <i className="material-icons">close</i>
            Reset
          </button>
        </div>
      </SidebarRight>

      {showModalEdit && (
        <Modal close={() => setShowModalEdit(false)}>
          <UserEdit userInit={editItem} list={list} setList={setList} close={() => setShowModalEdit(false)}></UserEdit>
        </Modal>
      )}
    </>
  );
}
